import React, { FC } from 'react';
import { isBrandAC, isBrandAP, FINANCE_DETAIL_VIEW, isMarketGB } from '../../../constants/main';
import { FEATURES_LIST, IFinanceWidgetButtonType } from '../../../context/featureSwitch';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import useTranslations from '@hooks/useTranslations';
import { renderPromotionalText } from '@utils/Price.utils';
import { Icons } from '../../Icon';
import IconButton from '../../IconButton';
import PriceSuffix from '../../PriceSuffix';
import { PriceBoxProps } from './index';
import { HandledComponentError } from '@components/ErrorHandling';
import AccessibleButton from '@components/Button/AccessibleButton';
import { getFormattedOfferValidityDate } from '@utils/Car.utils';

const PriceBoxTemplate: FC<PriceBoxProps> = ({
    className,
    basePrice,
    monthlyPrice,
    catalogPrice,
    depositPrice,
    calculateSummaryData,
    promotionalText = null,
    itemId,
    priceBoxWrapperStyle,
    amountRounding,
    childRef,
    financeDetailView,
    financeDetailText,
    apr,
    hasMonthlyPrices,
    importInputs,
    tabIndex = 0,
    index,
}) => {
    try {
        const { t } = useTranslations();
        const { formatMoney } = useMoneyFormatter();
        const isFinancePriceAvailable = monthlyPrice || monthlyPrice === 0;
        const isCataloguePriceFeatureSwitchEnabled = useFeatureSwitchEnabled(
            FEATURES_LIST.FEATURE_SWITCH_DISPLAY_CATALOGUE_PRICE
        );
        const isCataloguePriceEqualToStorePrice = catalogPrice && catalogPrice === basePrice;
        const isFinanceOverlayForcedToLink: boolean = useFeatureSwitchEnabled(
            FEATURES_LIST.FEATURE_SWITCH_FORCE_FINANCE_WIDGET_BUTTON,
            IFinanceWidgetButtonType.LINK
        );
        const isFinanceOverlayForcedToIcon: boolean = useFeatureSwitchEnabled(
            FEATURES_LIST.FEATURE_SWITCH_FORCE_FINANCE_WIDGET_BUTTON,
            IFinanceWidgetButtonType.ICON
        );
        const showFOIcon =
            (financeDetailView === FINANCE_DETAIL_VIEW.ICON || isFinanceOverlayForcedToIcon) &&
            !isFinanceOverlayForcedToLink;
        const showFOLink =
            (financeDetailView === FINANCE_DETAIL_VIEW.LINK || isFinanceOverlayForcedToLink) &&
            !isFinanceOverlayForcedToIcon;
        const showOfferValidityDate: boolean =
            useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_OFFER_VALIDITY_DATE_ENABLED) &&
            !useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_IS_FINANCE_DISABLED) &&
            hasMonthlyPrices;
        const offerValidityDate = getFormattedOfferValidityDate(importInputs);
        const offerValidityDateString =
            showOfferValidityDate && offerValidityDate ? `${t('basket.offerValidityDate')} ${offerValidityDate}` : '';

        return (
            <div className={`${className} priceBoxContainer`}>
                <div className="priceBoxContainerContentWrapper" ref={childRef?.priceBoxContainerRef}>
                    <div ref={childRef?.priceBoxCatalogRef} className="catalogPrice priceBoxCatalogRef">
                        {isCataloguePriceFeatureSwitchEnabled && !isCataloguePriceEqualToStorePrice && catalogPrice ? (
                            <>
                                <span className="catalogPriceLabel">{t('offerListItem.catalogPriceLabel')}</span>
                                <span className="catalogPriceAmount">{formatMoney(catalogPrice, true)}</span>
                                <PriceSuffix hideAsterisk />
                            </>
                        ) : null}
                    </div>
                    <div
                        style={priceBoxWrapperStyle}
                        ref={childRef?.priceBoxRef}
                        className="priceBoxWrapper priceBoxWrapperRef"
                    >
                        <div className="price" data-testid={itemId ? `TESTING_TOTAL_PRICE_${itemId}` : ''}>
                            <div
                                ref={childRef?.financeLabelContainerRef}
                                className="financeLabel financeLabelContainerRef"
                            >
                                {isCataloguePriceEqualToStorePrice ? (
                                    <span className="priceLabel priceFromLabel">
                                        {t('offerListItem.standardPriceText')}
                                    </span>
                                ) : (
                                    <span className="priceLabel specialPriceLabel">
                                        {t('offerListItem.specialPriceText')}
                                    </span>
                                )}
                                <span className="priceFromLabel">{t('nameplate.priceFrom')}</span>
                            </div>
                            <div className="price">
                                <div className="cashPrice">
                                    <span>{formatMoney(basePrice, true)}</span>
                                    <PriceSuffix checkPrioritizedTranslation hideAsterisk />
                                </div>

                                {isFinancePriceAvailable && (
                                    <div>
                                        <div className="priceConjunction">
                                            <span>{t('offerListItem.financePrice.conjunction')}</span>
                                        </div>

                                        <div className="monthlyPrice">
                                            <span>{formatMoney(monthlyPrice, false, '', amountRounding)}</span>
                                            <PriceSuffix checkPrioritizedTranslation isMonthlyPrice />
                                            {showFOIcon && (
                                                <IconButton
                                                    className="infoIcon"
                                                    icon={isBrandAC || isBrandAP ? Icons.InfoCircle : Icons.Info}
                                                    width={16}
                                                    height={16}
                                                    tabIndex={tabIndex}
                                                    onClick={calculateSummaryData}
                                                    dataTestId={itemId ? `TESTING_INFOICON_${itemId}` : ''}
                                                    altId={`svgImgSeeFinanceDetails-${index}`}
                                                    altText={financeDetailText}
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="priceNotice" data-testid={itemId ? `TESTING_PRICE_LEGAL_TEXT_${itemId}` : ''}>
                            {isFinancePriceAvailable &&
                                renderPromotionalText(
                                    promotionalText,
                                    t('price.pattern'),
                                    depositPrice,
                                    null,
                                    null,
                                    null,
                                    offerValidityDateString,
                                    { apr }
                                )}
                        </div>

                        {hasMonthlyPrices && showFOLink && (
                            <div className="apr">
                                <AccessibleButton className="aprDescription" onClick={calculateSummaryData}>
                                    {isMarketGB || financeDetailText === 'finance_detail_text_token'
                                        ? t('landingPage.solCard.bonus.link')
                                        : financeDetailText || t('financeOverlayButton.force.link.text')}
                                </AccessibleButton>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    } catch (e) {
        return <HandledComponentError error={e} />;
    }
};

export default PriceBoxTemplate;
